// The live frame at the playhead, its framing label and who is speaking. The episode header shows it small.
import { memo, type KeyboardEvent, type ReactNode } from "react";
import { timelineFrameAt } from "../../engine/timelineFrame";
import { findAsset } from "../../model/library";
import { cameraAt, castOf, lineAt, pictureAt, type TimelineEpisode } from "../../model/timeline";
import { FrameSvg } from "../../render/FrameSvg";
import { useLibrary } from "../../library/store";
import { FitFrame, Icon, useIdleClock } from "../common";

export const fmtT = (s: number) => {
  const tenths = Math.floor(Math.max(0, s) * 10 + 1e-6);
  return `${Math.floor(tenths / 600)}:${String(Math.floor((tenths % 600) / 10)).padStart(2, "0")}.${tenths % 10}`;
};

const FRAMING = { whole: "Whole picture", medium: "Medium", close: "Close-up", free: "Your framing" };

export const Stage = memo(function Stage({ ep, t, onAddPicture, onFull, corner }: { ep: TimelineEpisode; t: number; onAddPicture: () => void; onFull?: () => void; corner?: ReactNode }) {
  const lib = useLibrary();
  const clock = useIdleClock();
  const empty = !pictureAt(ep, t); // no pictures at all, or a gap where one was deleted
  const none = ep.pictures.length === 0;
  const cam = cameraAt(ep, t);
  const name = (id: string) => findAsset(lib, id)?.name ?? "Someone";
  const speaking = castOf(ep).filter(c => lineAt(ep, c, t)).map(name);
  const label = cam.on && cam.framing !== "whole" && cam.framing !== "free" ? `${FRAMING[cam.framing]} · ${name(cam.on)}` : FRAMING[cam.framing];
  return (
    <div className="tl-stage">
      {corner}
      <FitFrame className={empty ? "tl-empty-frame" : undefined}
        {...(empty ? { role: "button", tabIndex: 0, "aria-label": none ? "Add a picture to start" : "Add a picture here", onClick: onAddPicture, onKeyDown: (e: KeyboardEvent<HTMLDivElement>) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onAddPicture(); } } } : {})}>
        {empty ? <span>{none ? "Add a picture to start" : "No picture here. Add one"}</span> : (
          <>
            <FrameSvg id="tl-stage" frame={timelineFrameAt(ep, t, clock)} />
            <span className="tl-frame-label">{label}</span>
            {speaking.length > 0 && <span className="tl-caption">{speaking.join(" and ")} speaking</span>}
            {onFull && <button className="tl-full-btn" onClick={onFull} aria-label="Play full screen" title="Play full screen"><Icon name="expand" size={18} /></button>}
          </>
        )}
      </FitFrame>
    </div>
  );
});
